/**
 * The product axis — §1.4, roadmap 10.1.
 *
 * **Two navigation axes, and they must not share a control.** `portals.tsx`
 * answers *who am I acting as* (learner, instructor, ops) and lives in the
 * profile menu. This file answers *what am I browsing* (education, events,
 * marketplace) and is the primary navigation. Merging them produces a control
 * that answers neither question.
 *
 * Data rather than markup, for the same reason `PORTALS` is: the shape is read
 * by the desktop bar, the mobile drawer and the tests, and three copies of a
 * list drift.
 */
import {
  CalendarDays,
  GraduationCap,
  House,
  ShieldCheck,
  Store,
  type LucideIcon,
} from 'lucide-react'

import type { Role } from '@/shared/auth/roles'

export type ProductItem = {
  id: string
  /** Key in the `common` bundle. Never a literal — lint fails the build. */
  labelKey: string
  to: string
  /**
   * Hidden unless the user holds this role. Used for the one item that is a
   * *role* question inside a product menu — "organize events" — rather than
   * splitting it into its own product.
   */
  requiresRole?: Role
  /**
   * Hidden from anyone holding this role — the inverse of `requiresRole`.
   *
   * ⚠️ **A rendering rule, never a permission.** Added 30 August 2026 for the
   * platform admin, who was being offered "My tickets", "My listings", "List a
   * space" and "My enquiries" — four personal surfaces belonging to a job they
   * do not do. An admin moderates the marketplace; they do not rent out their
   * own studio through it.
   *
   * The **route keeps working**. Someone who holds `platform_admin` *and*
   * genuinely owns a listing can still open `/me/listings` by URL and use it
   * normally; the server decides what is allowed and is untouched by this
   * (`shared/auth/roles.ts`: holding a role here "is only ever used to decide
   * what to render"). This hides a door, it does not lock one.
   */
  hiddenForRole?: Role
  /**
   * Rendered, but not a link. The phases that build events and the marketplace
   * have not landed; roadmap 10.1 says show them disabled rather than hide
   * them or link to a 404. Hiding them makes the product look smaller than it
   * is; a dead link makes it look broken.
   */
  disabled?: boolean
}

export type ProductDefinition = {
  id: 'home' | 'education' | 'events' | 'marketplace' | 'ops'
  labelKey: string
  icon: LucideIcon
  /**
   * Where the trigger itself goes, and what marks the product active.
   *
   * ⚠️ **Only meaningful when `disabled` is unset.** A product whose landing
   * route has not been built must not carry a live `to` — see `disabled`.
   */
  to: string
  /** Matched as a prefix to decide the active product. */
  match: string[]
  /**
   * The product's own landing route does not exist yet, so `to` must never be
   * followed.
   *
   * **Nothing sets it today.** Events carried it while `/events` resolved to
   * the not-found screen; roadmap 15.8 built that route and the flag came off
   * with it. The field stays because the situation recurs — a product is
   * announced in the navigation before its landing page is built, and the
   * dropdown must still **open** rather than the product being hidden, or the
   * platform looks smaller than it is.
   *
   * ⚠️ The trigger happens not to navigate — it is a menu button, and `to` is
   * read by nothing. This flag is what keeps that safe: the next person who
   * makes the trigger a link has a field telling them which products have
   * somewhere to go, rather than a `to` that looks fine and lands on "Page not
   * found".
   */
  disabled?: boolean
  items: ProductItem[]
  /**
   * Shown beside the label — the education "Coming soon" pill while the flag
   * is off. Cleared when the feature turns on.
   */
  badgeKey?: string
  /**
   * The whole product is hidden unless the user holds this role.
   *
   * ⚠️ **Only Ops uses it, and that is deliberate.** CLAUDE.md is explicit
   * that the two navigation axes must not merge: *role* (which portal) lives
   * in the profile menu, *product* (what am I browsing) is the primary
   * navigation. Ops is the one place the distinction collapses — for a
   * platform admin, moderation **is** the product, and it was the hardest
   * thing on the platform to reach: two clicks deep inside the profile menu,
   * behind the role switcher.
   *
   * This is not licence to promote the other portals. An instructor's course
   * authoring is still a role they switch into, not a product they browse.
   */
  requiresRole?: Role
  /**
   * The whole product is hidden from anyone holding this role — the inverse of
   * `requiresRole`, and the product-level twin of `ProductItem.hiddenForRole`.
   *
   * ⚠️ **Both users of it are the platform admin** (30 August 2026):
   * *Marketplace*, because once browsing was removed its menu had nothing left
   * in it at all — an admin moderates listings from the ops queue's review
   * panel, which already carries the photographs, both title columns, price,
   * uses and the withheld detail fields; and *Education*, whose only item is
   * an announcement page for a product that is switched off.
   *
   * ⚠️ **Hiding a product hides its menu, never its routes.** `/listings` and
   * `/education` stay mounted and work by URL for an admin who wants them.
   * This is a rendering rule, exactly like the item-level flag.
   */
  hiddenForRole?: Role
  /**
   * Render the product as a direct link instead of a dropdown.
   *
   * ⚠️ A menu holding one item is friction, not navigation. For an admin the
   * Events product is exactly that — "Organize events" and nothing else, since
   * browsing and My tickets are both hidden from them — so it becomes a link
   * straight to that item's destination. Everyone else still gets the
   * dropdown, because they see two or three items in it.
   *
   * Computed rather than declared: see `productsFor`, which sets it when the
   * visible item count collapses to one. Declaring it by hand would be a
   * second source of truth for "how many items are there".
   */
  directTo?: string
}

/**
 * Education while `features.education` is **false**.
 *
 * It stays visible. Hiding it defeats the announcement page, which is the whole
 * point of the Phase 9 stand-down — the product is *announced*, not concealed.
 */
/**
 * Home — the front door, and the first thing in the bar.
 *
 * ⚠️ **Not a product, and it lives here anyway.** This file's own docstring
 * says the axis answers *what am I browsing*, and "home" is not an answer to
 * that. It is here because this list is what the desktop bar, the mobile
 * drawer and the nav tests all read, and a second list beside it would drift
 * from this one — which is the reason the file is data rather than markup in
 * the first place. The comment is the price of the shortcut.
 *
 * ⚠️ **It exists because the logo was the only way home.** The product owner:
 * *"the home page is only accessable through nextscene logo"*. A logo that
 * navigates is a convention, not an affordance — nothing tells a first-time
 * visitor it is clickable, and with Education sitting first the bar read as
 * though education were the default destination. It is not: `postLoginPath`
 * has always returned `/`, and `/` has rendered the discovery homepage since
 * Phase 20 replaced the education placeholder there.
 *
 * **Visible to everyone, platform admins included** (product owner, 12
 * September 2026). The personal items carry `hiddenForRole` because an admin
 * does not rent out a studio; a homepage is not a role question, and the logo
 * already takes them there.
 *
 * One item, so `productsFor` collapses it to a direct link and the bar renders
 * a plain button rather than a dropdown holding a single row.
 */
const HOME: ProductDefinition = {
  id: 'home',
  labelKey: 'product.home.label',
  icon: House,
  to: '/',
  // ⚠️ **`['/']` is already exact here, and that is worth knowing before
  // anyone "fixes" it.** `activeProduct` tests `pathname === prefix ||
  // pathname.startsWith(prefix + '/')`. For '/' the first arm matches only the
  // homepage itself, and the second would need a path beginning '//', which no
  // route produces. So Home lights up on '/' alone rather than on every route
  // in the application, with no special case.
  match: ['/'],

  items: [{ id: 'home', labelKey: 'product.home.browse', to: '/' }],
}

const EDUCATION_OFF: ProductDefinition = {
  id: 'education',
  labelKey: 'product.education.label',
  icon: GraduationCap,
  to: '/education',
  match: ['/education', '/courses'],
  badgeKey: 'product.education.comingSoon',
  // ⚠️ **Hidden from platform admins** (30 August 2026), in both the flag-off
  // and flag-on shapes. Education is switched off, so what an admin is offered
  // here is an announcement page for a product nobody is building — and the
  // admin bar exists to hold the two things they actually use. Everyone else
  // still sees it, badge and all, because *announcing* the product is the
  // whole point of the Phase 9 stand-down.
  hiddenForRole: 'platform_admin',

  items: [{ id: 'announcement', labelKey: 'product.education.announcement', to: '/education' }],
}

/**
 * Education with the flag on — a normal product entry, no badge.
 *
 * ⚠️ `myLearning` is **disabled**, found by the nav-walking test added with
 * Gap 23: `/me/courses` has no route in `router.tsx` and never had one. The
 * learner course surfaces are on the never-built list (CLAUDE.md, scope
 * guardrails), so this is not a route waiting to be wired — it is a
 * destination that does not exist.
 *
 * It only bites when `features.education` is **on**, which it is in local
 * development and in the test defaults but not in production. That is exactly
 * why it survived: the flag that hides the product also hides its dead link.
 */
const EDUCATION_ON: ProductDefinition = {
  id: 'education',
  labelKey: 'product.education.label',
  icon: GraduationCap,
  to: '/courses',
  match: ['/education', '/courses'],
  // ⚠️ **Hidden from platform admins** (30 August 2026), in both the flag-off
  // and flag-on shapes. Education is switched off, so what an admin is offered
  // here is an announcement page for a product nobody is building — and the
  // admin bar exists to hold the two things they actually use. Everyone else
  // still sees it, badge and all, because *announcing* the product is the
  // whole point of the Phase 9 stand-down.
  hiddenForRole: 'platform_admin',

  items: [
    { id: 'browse', labelKey: 'product.education.browse', to: '/courses' },
    {
      id: 'myLearning',
      labelKey: 'product.education.myLearning',
      to: '/me/courses',
      disabled: true,
    },
  ],
}

/**
 * Phase 15, whole. The organizer's half landed in 15b; the public half —
 * discovery, the event page and my tickets — landed in 15.8.
 *
 * ⚠️ **Nothing here is disabled any more, and that is the point of Gap 23.**
 * The product and its `browse` child were both `disabled` because `/events`
 * resolved to "Page not found"; `router.tsx` now routes `/events`,
 * `/events/:id` and `/me/tickets` for real. The flags came off together with
 * the routes going in, because a `disabled` flag left behind after its route
 * exists is a link the product hides from itself.
 *
 * `myTickets` is behind `RequireAuth`. It stays a live link rather than a
 * role-gated one: an attendee is any account holder, and a signed-out visitor
 * who follows it gets the sign-in screen, which is the right answer.
 */
const EVENTS: ProductDefinition = {
  id: 'events',
  labelKey: 'product.events.label',
  icon: CalendarDays,
  to: '/events',
  match: ['/events', '/me/tickets', '/me/orders'],
  items: [
    // ⚠️ Hidden from platform admins (30 August 2026). An admin *runs* the
    // calendar rather than shopping it — every event on `/events` is one they
    // created and can reach through `/organize`, so browsing is a second door
    // to their own work. The route stays mounted and opens by URL.
    {
      id: 'browse',
      labelKey: 'product.events.browse',
      to: '/events',
      hiddenForRole: 'platform_admin',
    },
    // Hidden from platform admins — see `hiddenForRole`. An admin approves
    // events; they do not hold tickets to them.
    {
      id: 'myTickets',
      labelKey: 'product.events.myTickets',
      to: '/me/tickets',
      hiddenForRole: 'platform_admin',
    },
    // Roadmap 16.10. ⚠️ **Hidden from platform admins**, same reasoning as
    // `myTickets` above and the `mine`/`create`/`enquiries` marketplace items
    // below: an admin moderates orders from `/ops/orders`; they do not place
    // one themselves. Listed under Events rather than a second copy under
    // Marketplace — `/me/orders` covers both ticket and booking purchases,
    // and one entry point avoids the same screen appearing in two menus.
    //
    // ⚠️ **Also dropped whenever `payments` is off** — fix round 1 on Task 4.
    // `productsFor` below removes this item entirely rather than adding a
    // per-item payments flag beside `hiddenForRole`: with payments off from
    // day one, no order can exist yet, so the item would open on a
    // permanently empty list whose one action (the receipt link) is already
    // a dead link (`OrdersScreen.tsx`). Accepted cost: a deployment that
    // switches payments off *after* orders exist loses the nav entry to its
    // own past orders — the data and the `/me/orders` route are both intact,
    // just not reachable from the menu.
    {
      id: 'orders',
      labelKey: 'product.events.orders',
      to: '/me/orders',
      hiddenForRole: 'platform_admin',
    },
    {
      id: 'organize',
      labelKey: 'product.events.organize',
      to: '/organize',
      // A *role* question inside a product menu — hidden from everyone who
      // cannot use it rather than disabled.
      //
      // ⚠️ **`platform_admin` since 30 August 2026.** It read
      // `event_organizer` until the product owner removed that role outright:
      // *"the system admin is the only allowed person to create an event"*.
      //
      // ⚠️ **This is the one events item an admin still sees**, and that is
      // deliberate. `myTickets` above is `hiddenForRole: 'platform_admin'`
      // because holding a ticket is not an admin's job — but *running* the
      // events is now exactly their job, so the same person who is denied the
      // personal surface is the only person offered this one.
      requiresRole: 'platform_admin',
    },
  ],
}

/**
 * Phases 12–14. Two listing kinds since 20 August 2026 — a browse entry each,
 * because a seeker looking for a lens and a seeker looking for a studio are
 * running different searches.
 */
const MARKETPLACE: ProductDefinition = {
  id: 'marketplace',
  // ⚠️ **Hidden from platform admins entirely** (30 August 2026), not item by
  // item. Every one of its items is already hidden from them — the three
  // personal surfaces since the admin-navigation ruling, and browsing since
  // this one — so leaving the product visible would open a menu onto nothing,
  // which reads as broken rather than as empty.
  //
  // An admin reviews listings from the ops queue's own panel, which carries
  // the photographs, both title columns, price, uses and the detail fields
  // that are withheld from every public serializer. `/listings` stays mounted
  // for anyone who wants it by URL.
  hiddenForRole: 'platform_admin',
  labelKey: 'product.marketplace.label',
  icon: Store,
  to: '/listings',
  match: ['/listings'],
  items: [
    // Live since roadmap 14.1 — `/listings` browses for real.
    { id: 'spaces', labelKey: 'product.marketplace.spaces', to: '/listings?kind=space' },
    { id: 'equipment', labelKey: 'product.marketplace.equipment', to: '/listings?kind=equipment' },
    // Live since roadmap 14b — the owner screens and the create wizard exist.
    // Both are behind `RequireAuth`; a signed-out visitor who follows one gets
    // the sign-in screen, which is the right answer rather than a hidden link.
    // ⚠️ All three hidden from platform admins (30 August 2026). An admin
    // moderates listings; they do not own one, list one, or enquire about one.
    // Browsing stays — a reviewer needs to see the catalogue as a seeker does.
    {
      id: 'mine',
      labelKey: 'product.marketplace.mine',
      to: '/me/listings',
      hiddenForRole: 'platform_admin',
    },
    {
      id: 'create',
      labelKey: 'product.marketplace.create',
      to: '/me/listings/new',
      hiddenForRole: 'platform_admin',
    },
    // The seeker's half. `mine` above is what this account *owns*; this is
    // what it has *asked for* — two different questions, and an accepted
    // booking was invisible without it.
    {
      id: 'enquiries',
      labelKey: 'product.marketplace.enquiries',
      to: '/me/enquiries',
      hiddenForRole: 'platform_admin',
    },
  ],
}

/**
 * The ops console — the platform admin's actual job, in the primary
 * navigation since 30 August 2026.
 *
 * ⚠️ **It was previously reachable only through the profile menu**, which put
 * the one surface an admin uses all day behind the control for *switching
 * roles* — while the same admin was offered "My tickets" and "List a space"
 * in the primary bar, for jobs they never do. The menus were exactly inverted.
 *
 * Last in the order rather than first: the product bar keeps the shape
 * everyone else knows, and the admin tool is a distinct trailing item rather
 * than something that reshuffles the navigation depending on who is looking.
 *
 * English-only labels come from the `common` bundle like every other product;
 * the *screens* behind them are English-only by CLAUDE.md, but the navigation
 * that reaches them is part of the shared shell and is translated.
 */
const OPS: ProductDefinition = {
  id: 'ops',
  labelKey: 'product.ops.label',
  icon: ShieldCheck,
  to: '/ops',
  match: ['/ops'],
  requiresRole: 'platform_admin',
  items: [
    { id: 'approvals', labelKey: 'product.ops.approvals', to: '/ops' },
    // Roadmap 18.1. ⚠️ **A screen reachable only from inside its own portal is
    // a screen nobody finds.** This tab existed in `portals/ops/index.tsx` for
    // a while before it was listed here, so the only route to it was landing on
    // Approvals first and noticing the tab strip — which is exactly the
    // "unfindable is the same as broken" failure the listing wizard's Next
    // button was added for.
    { id: 'accounts', labelKey: 'product.ops.accounts', to: '/ops/users' },
    { id: 'auditLog', labelKey: 'product.ops.auditLog', to: '/ops/audit-log' },
    // Roadmap 16.10. Listed here for the same reason `accounts` is — a tab
    // that exists only in `portals/ops/index.tsx`'s own strip is unfindable
    // until whoever needs it happens to land on Approvals first.
    { id: 'orders', labelKey: 'product.ops.orders', to: '/ops/orders' },
    // ⚠️ **The `roles` item was removed on 30 August 2026** along with
    // `/ops/roles`, its screen and its endpoint. It pointed at a console that
    // granted exactly one role, `event_organizer`, which the product owner
    // removed. It comes back with the deferred door role — see
    // `apps/ops/urls.py` for the security note that must come back with it.
  ],
}

/**
 * The products, in navigation order.
 *
 * Education first because it is the oldest surface and the one people arrive
 * looking for; the marketplace last because it is the newest. The profile is
 * not here — it is the account menu, at the inline end of the header.
 */
export function productsFor({
  education,
  payments,
  hasRole,
}: {
  education: boolean
  /**
   * ⚠️ **Added fix round 1 on Task 4, required like `education` rather than
   * optional** — a caller has to say what it means, the same reason
   * `education` carries no default. Drops the `orders` item from Events (see
   * its comment above) when off; nothing else in the navigation reads it
   * today.
   */
  payments: boolean
  /**
   * ⚠️ Optional so every existing caller and test keeps working unchanged.
   * Omitted means "no roles known", which hides Ops — the safe default for a
   * signed-out visitor, who must never see an admin entry.
   */
  hasRole?: (role: Role) => boolean
}): ProductDefinition[] {
  // ⚠️ Filtered here, before `EVENTS` ever reaches `visibleItemsFor` — so the
  // desktop bar, the mobile drawer and this file's own `directTo` collapse
  // below all agree without any of them having to know about `payments`
  // individually. A per-item flag threaded through `visibleItemsFor` would
  // work too, but it would mean widening that function's signature (and both
  // of `ProductNav.tsx`'s call sites) for a rule that only one item, in one
  // product, currently needs.
  const events = payments
    ? EVENTS
    : { ...EVENTS, items: EVENTS.items.filter((item) => item.id !== 'orders') }

  const all = [HOME, education ? EDUCATION_ON : EDUCATION_OFF, events, MARKETPLACE, OPS]

  const held = (role: Role) => hasRole?.(role) ?? false

  return all
    .filter((product) => !product.requiresRole || held(product.requiresRole))
    .filter((product) => !product.hiddenForRole || !held(product.hiddenForRole))
    .map((product) => {
      // ⚠️ **`directTo` is computed from what this viewer can actually see**,
      // never declared on the definition. A product whose items are role-gated
      // has a different shape per person: an admin sees one item under Events
      // and everyone else sees three, so "is this a dropdown?" cannot be a
      // fixed property. Deriving it here keeps one source of truth — the same
      // visibility rules `ProductNav` applies when it renders the menu.
      const visible = product.items.filter(
        (item) =>
          (!item.requiresRole || held(item.requiresRole)) &&
          (!item.hiddenForRole || !held(item.hiddenForRole)),
      )
      // Only a *live* single item collapses. A lone disabled item still needs
      // its menu, because the menu is where the "Soon" label lives.
      return visible.length === 1 && !visible[0]!.disabled
        ? { ...product, directTo: visible[0]!.to }
        : product
    })
}

/**
 * The items inside one product that this viewer may see.
 *
 * Two gates, in opposite directions:
 *
 * * `requiresRole` — hide unless held. "Organize events", for someone who is
 *   not an admin.
 * * `hiddenForRole` — hide **because** held. The platform admin's own personal
 *   surfaces.
 *
 * ⚠️ **Here rather than in `ProductNav`, because it now has three callers.**
 * The desktop bar, the mobile drawer and — since roadmap 20.2 — the homepage's
 * shortcuts all have to answer "which of these may this person see?", and the
 * answer has to be the same in all three. It lived as a private function in
 * `ProductNav.tsx` while two of the callers were in that file; the homepage
 * reimplementing it would have been a fourth statement of a rule that has
 * already been rewritten twice.
 *
 * ⚠️ **Both flags are *rendering* rules, never permissions.** The routes stay
 * mounted and the server is untouched — see `shared/auth/roles.ts`.
 */
export function visibleItemsFor(
  product: ProductDefinition,
  hasRole: (role: Role) => boolean,
): ProductItem[] {
  return product.items.filter(
    (item) =>
      (!item.requiresRole || hasRole(item.requiresRole)) &&
      (!item.hiddenForRole || !hasRole(item.hiddenForRole)),
  )
}

/** The product whose `match` prefixes the current path, if any. */
export function activeProduct(
  products: ProductDefinition[],
  pathname: string,
): ProductDefinition | undefined {
  return products.find((product) =>
    product.match.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)),
  )
}
